import React, { useMemo } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import {
  SDataTable,
  type SDataTableColumnDef,
} from '@modules/ui-components/dialogs/SDataTable';
import { useGoogleAdsConnect } from '../hooks/useGoogleAdsConnect';
import type { GoogleAdsConnectApi } from '../services/GoogleAdsConnectService';
import { GOOGLE_ADS_TEST_IDS } from '../testIds';
import type { GoogleAdsReportingAccount } from '../types';

export interface GoogleAdsIntegrationContentProps {
  projectId: string;
  showHeader?: boolean;
  service?: GoogleAdsConnectApi;
}

function formatCustomerId(customerId: string): string {
  return /^\d{10}$/u.test(customerId)
    ? `${customerId.slice(0, 3)}-${customerId.slice(3, 6)}-${customerId.slice(6)}`
    : customerId;
}

function accessPath(account: GoogleAdsReportingAccount): string {
  const context = account.accessProvenance.accessContext;
  return context.kind === 'manager'
    ? `Via manager ${formatCustomerId(context.managerAccountId)}`
    : 'Direct';
}

export function GoogleAdsIntegrationContent({
  projectId,
  showHeader = false,
  service,
}: GoogleAdsIntegrationContentProps) {
  const googleAds = useGoogleAdsConnect(projectId, service);
  const columns = useMemo<SDataTableColumnDef<GoogleAdsReportingAccount>[]>(() => [
    {
      accessorKey: 'displayName',
      header: 'Account',
      size: 150,
      minSize: 140,
      grow: false,
      Cell: ({ row }) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {row.original.displayName || 'Unnamed account'}
        </Typography>
      ),
    },
    {
      id: 'customerId',
      header: 'Customer ID',
      size: 145,
      minSize: 135,
      grow: false,
      accessorFn: (row) => row.identity.providerAccountId,
      Cell: ({ row }) => formatCustomerId(row.original.identity.providerAccountId),
    },
    {
      id: 'accessPath',
      header: 'Access path',
      size: 225,
      minSize: 205,
      grow: false,
      accessorFn: accessPath,
      Cell: ({ row }) => (
        <Chip
          size="small"
          variant="outlined"
          label={accessPath(row.original)}
        />
      ),
    },
    {
      accessorKey: 'currency',
      header: 'Currency',
      size: 120,
      minSize: 105,
      grow: false,
      Cell: ({ cell }) => cell.getValue<string>() || '—',
    },
    {
      accessorKey: 'timeZone',
      header: 'Time zone',
      size: 130,
      minSize: 120,
      grow: false,
      Cell: ({ cell }) => cell.getValue<string>() || '—',
    },
  ], []);

  if (googleAds.loading) {
    return (
      <Box
        data-testid={GOOGLE_ADS_TEST_IDS.loading}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 5 }}
      >
        <CircularProgress size={32} aria-label="Loading Google Ads connection" />
      </Box>
    );
  }

  const activeConnection = googleAds.activeConnection;
  const connectorEnabled = googleAds.runtime?.connectorEnabled === true;
  const selectedCount = googleAds.selectedCustomerIds.length;
  const saveDisabled = !googleAds.isDirty
    || selectedCount === 0
    || selectedCount > 100
    || googleAds.action !== null;
  const selectionHelp = selectedCount === 0
    ? 'Select at least one account to save.'
    : selectedCount > 100
      ? 'Select no more than 100 accounts.'
      : googleAds.isDirty
        ? `${selectedCount} account${selectedCount === 1 ? '' : 's'} ready to save.`
        : 'Saved selection is current.';

  return (
    <Stack spacing={2.5} data-testid={GOOGLE_ADS_TEST_IDS.root}>
      {showHeader && (
        <Box>
          <Typography variant="h6">Google Ads</Typography>
          <Typography variant="body2" color="text.secondary">
            Choose which advertiser accounts Segmently may read for reporting.
          </Typography>
        </Box>
      )}

      {googleAds.notice && (
        <Alert severity="success" onClose={googleAds.clearNotice}>
          {googleAds.notice}
        </Alert>
      )}
      {googleAds.error && (
        <Alert
          severity="error"
          data-testid={GOOGLE_ADS_TEST_IDS.statusAlert}
          onClose={googleAds.clearError}
          action={!googleAds.isDirty ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => activeConnection?.connected
                ? void googleAds.refreshAccounts()
                : void googleAds.refreshConnections()}
            >
              Retry
            </Button>
          ) : undefined}
        >
          {googleAds.error}
        </Alert>
      )}

      {!connectorEnabled && (
        <Alert severity={googleAds.runtime?.rollbackActive ? 'warning' : 'info'} data-testid={GOOGLE_ADS_TEST_IDS.statusAlert}>
          <AlertTitle>Google Ads connector is unavailable</AlertTitle>
          {googleAds.runtime?.rollbackActive
            ? 'The connector is temporarily paused by an operator safety control.'
            : 'An administrator must enable the read-only Google Ads connector before this project can connect.'}
        </Alert>
      )}

      {connectorEnabled && !activeConnection && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2} alignItems="flex-start">
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Connect Google Ads
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Authorize read-only reporting access. Segmently will not create or modify campaigns,
                budgets, targeting, audiences, or creatives.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={googleAds.action === 'connect' ? <CircularProgress size={16} color="inherit" /> : <AddLinkIcon />}
              disabled={googleAds.action !== null}
              onClick={() => void googleAds.connect()}
              data-testid={GOOGLE_ADS_TEST_IDS.connectButton}
            >
              Connect Google Ads
            </Button>
          </Stack>
        </Paper>
      )}

      {connectorEnabled
        && activeConnection
        && !activeConnection.connected
        && !activeConnection.needsReconnect && (
        <Alert
          severity="info"
          data-testid={GOOGLE_ADS_TEST_IDS.statusAlert}
          action={(
            <Button
              color="inherit"
              size="small"
              disabled={googleAds.action !== null}
              onClick={() => void googleAds.connect(activeConnection.connectionId)}
              data-testid={GOOGLE_ADS_TEST_IDS.reconnectButton}
            >
              Connect
            </Button>
          )}
        >
          <AlertTitle>{activeConnection.displayName || 'Google Ads connection'}</AlertTitle>
          This named connection is disconnected. Reconnect it to discover advertiser accounts.
        </Alert>
      )}

      {connectorEnabled && activeConnection?.needsReconnect && (
        <Alert
          severity="warning"
          data-testid={GOOGLE_ADS_TEST_IDS.statusAlert}
          action={(
            <Button
              color="inherit"
              size="small"
              disabled={googleAds.action !== null}
              onClick={() => void googleAds.connect(activeConnection.connectionId)}
              data-testid={GOOGLE_ADS_TEST_IDS.reconnectButton}
            >
              Reconnect
            </Button>
          )}
        >
          <AlertTitle>Google authorization needs attention</AlertTitle>
          Reconnect this grant before selecting accounts. Existing selections remain recorded.
        </Alert>
      )}

      {connectorEnabled && activeConnection?.connected && !activeConnection.needsReconnect && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {activeConnection.displayName || 'Google Ads connection'}
                  </Typography>
                  <Chip label={activeConnection.sourceApiVersion} size="small" variant="outlined" />
                  <Chip label="Read-only" size="small" color="primary" variant="outlined" />
                  <Chip
                    label={`${activeConnection.selectedCustomerIds.length} selected`}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Select only the advertiser accounts Segmently should include in reporting.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddLinkIcon />}
                  disabled={googleAds.isDirty || googleAds.action !== null}
                  onClick={() => void googleAds.connect()}
                  data-testid={GOOGLE_ADS_TEST_IDS.connectAnotherButton}
                >
                  Connect another
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<RefreshIcon />}
                  disabled={googleAds.isDirty || googleAds.accountsLoading || googleAds.action !== null}
                  onClick={() => void googleAds.refreshAccounts()}
                  data-testid={GOOGLE_ADS_TEST_IDS.refreshButton}
                >
                  Refresh
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<LinkOffIcon />}
                  disabled={googleAds.isDirty || googleAds.action !== null}
                  onClick={() => void googleAds.disconnect()}
                  data-testid={GOOGLE_ADS_TEST_IDS.disconnectButton}
                >
                  Disconnect
                </Button>
              </Stack>
            </Stack>

            {googleAds.connections.length > 1 && (
              <FormControl size="small" sx={{ maxWidth: 420 }} disabled={googleAds.isDirty || googleAds.action !== null}>
                <InputLabel id="google-ads-connection-label">Authorized connection</InputLabel>
                <Select
                  labelId="google-ads-connection-label"
                  label="Authorized connection"
                  value={googleAds.activeConnectionId}
                  onChange={(event) => googleAds.chooseConnection(event.target.value)}
                  data-testid={GOOGLE_ADS_TEST_IDS.connectionSelect}
                >
                  {googleAds.connections.map((connection) => (
                    <MenuItem key={connection.connectionId} value={connection.connectionId}>
                      {connection.displayName || connection.connectionId}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {googleAds.isDirty && (
              <Alert severity="info">
                Save or discard the current selection before refreshing, switching connections,
                connecting another grant, or disconnecting.
              </Alert>
            )}

            {googleAds.accountsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress size={32} aria-label="Loading Google Ads advertiser accounts" />
              </Box>
            ) : googleAds.accounts.length === 0 ? (
              <Alert severity="info">
                <AlertTitle>No advertiser accounts found</AlertTitle>
                This Google authorization does not currently expose a direct advertiser account or
                an eligible client account through a manager account.
              </Alert>
            ) : (
              <Box data-testid={GOOGLE_ADS_TEST_IDS.accountTable} sx={{ overflowX: 'auto' }}>
                <SDataTable<GoogleAdsReportingAccount>
                  columns={columns}
                  data={googleAds.accounts}
                  layoutMode="grid-no-grow"
                  enableTopToolbar={false}
                  enableSelectAll={googleAds.accounts.length <= 100}
                  getRowId={(row) => row.identity.providerAccountId}
                  enableRowSelection={(row) => (
                    googleAds.selectedCustomerIds.length < 100
                    || googleAds.rowSelection[row.original.identity.providerAccountId] === true
                  )}
                  enablePagination={googleAds.accounts.length > 25}
                  initialState={{ pagination: { pageIndex: 0, pageSize: 25 } }}
                  state={{ rowSelection: googleAds.rowSelection }}
                  onRowSelectionChange={googleAds.setRowSelection}
                  muiTableProps={{ size: 'small', sx: { minWidth: 830 } }}
                  muiTableContainerProps={{ sx: { maxHeight: 440 } }}
                />
              </Box>
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="body2"
                  color={selectedCount === 0 || selectedCount > 100 ? 'error' : 'text.secondary'}
                  data-testid={GOOGLE_ADS_TEST_IDS.selectedCount}
                >
                  {selectionHelp}
                </Typography>
                {googleAds.isDirty && (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={googleAds.discardChanges}
                    disabled={googleAds.action !== null}
                    data-testid={GOOGLE_ADS_TEST_IDS.discardButton}
                  >
                    Discard changes
                  </Button>
                )}
              </Stack>
              <Button
                variant="contained"
                startIcon={googleAds.action === 'save' ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                disabled={saveDisabled}
                onClick={() => void googleAds.saveSelection()}
                data-testid={GOOGLE_ADS_TEST_IDS.saveButton}
              >
                Save account selection
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

export default GoogleAdsIntegrationContent;
